/**
 * LinkedAccountsManager - Manage connected bank institutions
 * Allows users to view and disconnect their Plaid-linked bank accounts
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Unlink, Loader2, AlertTriangle, RefreshCw, Check, Plus, Link2 } from 'lucide-react';
import { apiGet, apiPost } from '@/utils/api';
import { toast } from 'sonner';

function LinkedAccountsManager() {
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(null); // item_id of disconnecting institution
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [showRelinkPrompt, setShowRelinkPrompt] = useState(false);

  useEffect(() => {
    fetchInstitutions();
  }, []);

  const fetchInstitutions = async () => {
    setLoading(true);
    try {
      const response = await apiGet('/plaid/connected-institutions');
      if (response.ok) {
        const data = await response.json();
        setInstitutions(data.institutions || []);
      }
    } catch (error) {
      console.error('Failed to fetch institutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectClick = (institution) => {
    setSelectedInstitution(institution);
    setShowConfirmModal(true);
  };

  const confirmDisconnect = async () => {
    if (!selectedInstitution) return;
    
    setDisconnecting(selectedInstitution.item_id);
    setShowConfirmModal(false);
    
    try {
      const response = await apiPost('/plaid/disconnect-institution', {
        item_id: selectedInstitution.item_id
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Bank disconnected successfully');
        // Remove from local state
        const remaining = institutions.filter(i => i.item_id !== selectedInstitution.item_id);
        setInstitutions(remaining);
        
        // Show re-link prompt if this was the last institution
        if (remaining.length === 0) {
          setShowRelinkPrompt(true);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to disconnect bank');
      }
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect bank');
    } finally {
      setDisconnecting(null);
      setSelectedInstitution(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleRelinkBank = () => {
    // Navigate to bills page which has Plaid integration
    navigate('/app/bills');
    toast.info('Connect your bank on the Bills page to auto-import transactions');
  };

  if (loading) {
    return (
      <div className="bg-[#243447] rounded-xl p-4 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#00d4ff] animate-spin" />
      </div>
    );
  }

  if (institutions.length === 0) {
    return (
      <div className="bg-[#243447] rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-gray-500" />
            <div>
              <div className="text-white font-bold">No Linked Banks</div>
              <div className="text-gray-400 text-sm">
                Link a bank account to automatically import your bills
              </div>
            </div>
          </div>
          <button
            onClick={handleRelinkBank}
            className="flex items-center gap-2 px-4 py-2 bg-[#00d4ff] text-[#1a2634] font-bold rounded-xl hover:bg-[#00d4ff]/90 transition-colors"
            data-testid="link-bank-btn"
          >
            <Link2 className="w-4 h-4" />
            <span>Link Bank</span>
          </button>
        </div>
        
        {/* Re-link prompt after disconnection */}
        {showRelinkPrompt && (
          <div className="mt-4 p-4 bg-[#1a2634] border border-[#00d4ff]/30 rounded-xl">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#00d4ff]/20 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-[#00d4ff]" />
              </div>
              <div>
                <div className="text-white font-bold">Ready to reconnect?</div>
                <div className="text-gray-400 text-sm">When you're ready to sync transactions again</div>
              </div>
            </div>
            <button
              onClick={handleRelinkBank}
              className="w-full mt-3 py-3 bg-gradient-to-r from-[#00d4ff] to-[#00a8cc] text-[#1a2634] font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              data-testid="relink-bank-btn"
            >
              <Plus className="w-4 h-4" />
              Re-link Your Bank
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#243447] rounded-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-[#00d4ff]" />
            <div>
              <div className="text-white font-bold">Linked Bank Accounts</div>
              <div className="text-gray-400 text-sm">{institutions.length} connected</div>
            </div>
          </div>
          <button
            onClick={fetchInstitutions}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            data-testid="refresh-institutions-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Institution List */}
        <div className="divide-y divide-white/10">
          {institutions.map((institution) => (
            <div 
              key={institution.item_id}
              className="p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#1a2634] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#00d4ff]" />
                </div>
                <div>
                  <div className="text-white font-medium">
                    {institution.institution_name || 'Bank Account'}
                  </div>
                  <div className="text-gray-500 text-xs">
                    Connected {formatDate(institution.linked_at)}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => handleDisconnectClick(institution)}
                disabled={disconnecting === institution.item_id}
                className="flex items-center gap-2 px-3 py-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-colors disabled:opacity-50"
                data-testid={`disconnect-btn-${institution.item_id}`}
              >
                {disconnecting === institution.item_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Unlink className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">Disconnect</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedInstitution && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a2634] border border-[#2a3a4a] rounded-2xl w-full max-w-md p-6">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-white text-center mb-2">
              Disconnect {selectedInstitution.institution_name}?
            </h2>
            
            <p className="text-gray-400 text-center mb-6">
              Disconnecting will stop all automatic transaction syncing for this bank. 
              You will need to re-link your bank to resume tracking.
            </p>

            {/* What happens section */}
            <div className="bg-[#243447] rounded-xl p-4 mb-6">
              <p className="text-gray-300 text-sm font-medium mb-2">What happens:</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  <span>Connection to {selectedInstitution.institution_name} will be removed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-400 mt-0.5">•</span>
                  <span>Automatic bill sync will stop</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-0.5">•</span>
                  <span>Your existing bills will remain (as manual entries)</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedInstitution(null);
                }}
                className="flex-1 py-3 bg-[#243447] text-white rounded-xl font-bold hover:bg-[#2a3a4a] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDisconnect}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-disconnect-btn"
              >
                <Unlink className="w-4 h-4" />
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LinkedAccountsManager;
